/**
 * Живое превью бренд-кита (§13).
 *
 * Рисуется теми же значениями, что уйдут в генераторы графики (§27):
 * расхождение «в превью одно, в ролике другое» — самая частая жалоба
 * на такие экраны (agents/api-looks-brand.md §5).
 */
import type { BrandKitView } from '@alterium/shared';
import { useTranslation } from 'react-i18next';

export function StylePreview({ kit }: { kit: BrandKitView }) {
  const { t } = useTranslation();
  const { colors, captions, cta, lowerThird, logo } = kit;

  const logoCorner = {
    'top-left': 'left-0 top-0',
    'top-right': 'right-0 top-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-right': 'bottom-0 right-0',
  }[logo.position];

  const captionPosition = {
    top: 'top-[12%]',
    center: 'top-1/2 -translate-y-1/2',
    bottom: 'bottom-[16%]',
  }[captions.position];

  return (
    <div
      className="relative aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-card border border-line"
      style={{ backgroundColor: colors.background }}
    >
      {/* Логотип с safe area §13 */}
      <div
        className={`absolute ${logoCorner} flex items-center justify-center`}
        style={{
          margin: `${logo.safeAreaPercent}%`,
          width: `${logo.sizePercent}%`,
          opacity: logo.opacity,
        }}
      >
        {kit.logoUrl ? (
          <img src={kit.logoUrl} alt="" className="w-full object-contain" />
        ) : (
          <div
            className="aspect-square w-full rounded-sm"
            style={{ backgroundColor: colors.primary, opacity: 0.35 }}
          />
        )}
      </div>

      {/* Lower third §13 */}
      {lowerThird.name || lowerThird.title ? (
        <div
          className={`absolute bottom-[32%] ${
            lowerThird.position === 'bottom-center'
              ? 'left-1/2 -translate-x-1/2 text-center'
              : 'left-[7%]'
          }`}
        >
          {lowerThird.style === 'bar' ? (
            <div className="mb-1 h-[3px] w-8" style={{ backgroundColor: colors.primary }} />
          ) : null}
          <p
            className="text-[13px] font-semibold leading-tight"
            style={{
              color: lowerThird.style === 'accent' ? colors.primary : colors.text,
              fontFamily: kit.typography.heading.family,
            }}
          >
            {lowerThird.name || t('style.previewName')}
          </p>
          <p
            className="text-[11px] leading-tight"
            style={{ color: colors.secondary, fontFamily: kit.typography.body.family }}
          >
            {lowerThird.title || t('style.lowerThird.role')}
          </p>
        </div>
      ) : null}

      {/* Титры §13, §28 — с активным словом */}
      {captions.enabled ? (
        <div className={`absolute inset-x-[7%] ${captionPosition}`}>
          <p
            className="text-center leading-snug"
            style={{
              color: captions.color,
              fontFamily: kit.typography.captions.family,
              fontWeight: kit.typography.captions.weight,
              fontSize: `${captions.sizePercent * 2.4}px`,
              textShadow:
                captions.background === 'shadow'
                  ? `0 2px 8px ${captions.backgroundColor}`
                  : 'none',
              backgroundColor:
                captions.background === 'solid' ? captions.backgroundColor : 'transparent',
              padding: captions.background === 'solid' ? '2px 6px' : undefined,
              borderRadius: captions.background === 'solid' ? 4 : undefined,
            }}
          >
            {t('style.previewCaptionsBefore')}{' '}
            <span style={{ color: captions.activeWordColor }}>{t('style.previewCaptionsWord')}</span>{' '}
            {t('style.previewCaptionsAfter')}
          </p>
        </div>
      ) : null}

      {/* CTA §13 */}
      <div className="absolute inset-x-[7%] bottom-[6%] flex justify-center">
        <span
          className="rounded-control px-3 py-1.5 text-[11px] font-medium"
          style={
            cta.buttonStyle === 'solid'
              ? { backgroundColor: cta.buttonColor, color: cta.buttonTextColor }
              : cta.buttonStyle === 'outline'
                ? { border: `1px solid ${cta.buttonColor}`, color: cta.buttonColor }
                : { color: cta.buttonColor }
          }
        >
          {cta.text}
        </span>
      </div>
    </div>
  );
}
